import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { collection, query, orderBy, onSnapshot, getDocs, doc, where } from 'firebase/firestore';
import { useFirebase } from '../contexts/FirebaseContext';
import { useFavorites } from '../contexts/FavoritesContext';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { Search, Star, Heart, UtensilsCrossed, Wine, Pizza, Flame, Home, Fish, Coffee, Star as StarIcon, ShoppingBag, Leaf, ShoppingCart, AlertCircle, Paintbrush, Droplet, Monitor, CircleX, Sparkles, GlassWater, Share, Cake, Wrench } from 'lucide-react';
import PromotionalVideosView from '../components/PromotionalVideosView';
import AdminsChoiceView from '../components/AdminsChoiceView';
import RestaurantMapModal from '../components/RestaurantMapModal';
import { ChefHeader, IslandPicker } from '../components/ios';
import CartSheet from '../components/ios/CartSheet';
import { loadCache, saveCache } from '../utils/dataCache';

// ── iOS MenuView mirror ─────────────────────────────────────────
// Layout order mirrors MenuView.swift body:
// ChefHeader("Menu") + island button → PromotionalVideosView →
// AdminsChoiceView (Featured Food / Featured Retail) → SearchBar →
// Food/Retail BusinessTypeButtons →
// MenuItemsView (restaurant-type sections w/ horizontal card carousels)
// + floating cart FAB → CartView sheet.
function MenuPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { db, firebaseError } = useFirebase();
  const { isFavorite, toggleFavorite } = useFavorites();
  const { getItemCount, items: cartItems } = useCart();
  const { currentUser } = useAuth();
  const [searchText, setSearchText] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedBusinessType, setSelectedBusinessType] = useState(() => {
    const urlType = searchParams.get('businessType');
    return urlType === 'retail' || urlType === 'food' ? urlType : 'food';
  });
  const [showCartSheet, setShowCartSheet] = useState(false);
  const [showMap, setShowMap] = useState(false);

  // Keep business type in URL so back navigation restores the right tab
  useEffect(() => {
    const currentType = searchParams.get('businessType');
    if (currentType !== selectedBusinessType) {
      setSearchParams({ businessType: selectedBusinessType }, { replace: true });
    }
  }, [selectedBusinessType, searchParams, setSearchParams]);

  // Default to St. Kitts and remember user's preference (IslandPicker writes the same key)
  const getInitialIsland = () => {
    if (typeof window === 'undefined') return 'saintKitts';
    return localStorage.getItem('chef_selected_island') || 'saintKitts';
  };
  const [selectedIsland, setSelectedIsland] = useState(getInitialIsland);
  const [menuItems, setMenuItems] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAppOffline, setIsAppOffline] = useState(false);

  // Available islands - only show individual islands, no 'all' option
  const islands = [
    { id: 'saintKitts', name: 'St. Kitts', flag: '🇰🇳', firestoreValue: 'St. Kitts' },
    { id: 'nevis', name: 'Nevis', flag: '🇰🇳', firestoreValue: 'Nevis' }
  ];

  // Get current selected island info
  const currentIsland = islands.find(i => i.id === selectedIsland) || islands[0];

  // Restaurant types - matching iOS MenuItemsView fullOrder exactly
  // (KFC first on St. Kitts, removed on Nevis)
  const restaurantTypes = [
    'KFC', 'Fast Food', 'Bar & Grill', 'Pizza', 'Bakery', 'Cafe', 'Juice Stand', 'BBQ Grill', 'Seafood Restaurant', 'Asian', 'Indian', 'Latin', 'Mexican', 'Sushi', 'Steak House', 'Local Restaurant', 'Roti/Patty', 'Vegetarian', 'Snack Boxes', 'Fish & Meat', 'Farmer', 'Supermarket', 'Retail Store', 'Electronics Store', 'Home Goods', 'Home Goods / Hardware Store', 'Beauty Supply', 'Perfume Store', 'Jewelry Store', 'Craft & Souvenirs', 'Liquor Store & Smoke Shop', 'Water Supply', 'Car Parts', 'Pharmacy'
  ];

  // Food-related restaurant types (matching iOS RestaurantType.isFoodRelated —
  // note Fish & Meat is retail on iOS)
  const foodRelatedTypes = new Set([
    'Fast Food', 'Bar & Grill', 'Pizza', 'KFC', 'BBQ Grill', 'Seafood Restaurant', 'Cafe', 'Bakery', 'Asian', 'Indian', 'Roti/Patty', 'Juice Stand', 'Vegetarian', 'Snack Boxes', 'Latin', 'Mexican', 'Sushi', 'Steak House', 'Local Restaurant'
  ]);

  // Check if restaurant type is food-related
  const isFoodRelated = (restaurantType) => {
    return foodRelatedTypes.has(restaurantType);
  };

  // Restaurants shown on the map — same island + active/accepting gate as
  // the menu items (mirrors the iOS sheet's filter).
  const mapRestaurants = useMemo(() => {
    return restaurants.filter(r =>
      r.island === currentIsland.firestoreValue &&
      r.isActive === true &&
      r.isAcceptingOrders === true
    );
  }, [restaurants, currentIsland.firestoreValue]);

  // Hydrate from the cache first (iOS CacheManager pattern) — renders
  // last-known restaurants + menu items instantly, then Firestore refreshes
  // in the background and updates both the UI and the cache.
  useEffect(() => {
    const cachedRestaurants = loadCache('restaurants');
    const cachedItems = loadCache('menuItems');
    if (cachedRestaurants?.length) setRestaurants(cachedRestaurants);
    if (cachedItems?.length) {
      setMenuItems(cachedItems);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!firebaseError && db) {
      loadAllRestaurants();
      monitorAppOfflineStatus();
    } else {
      setLoading(false);
    }
  }, [firebaseError, db]);

  // Remember island preference any time it changes
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('chef_selected_island', selectedIsland);
    }
  }, [selectedIsland]);

  // Monitor app offline status from settings/global document
  const monitorAppOfflineStatus = () => {
    if (!db) return;

    const settingsRef = doc(db, 'settings', 'global');
    const unsubscribe = onSnapshot(settingsRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.data();
        setIsAppOffline(data.isAppOffline || false);
      } else {
        setIsAppOffline(false);
      }
    }, (error) => {
      console.error('Error monitoring offline status:', error);
      setIsAppOffline(false);
    });

    return unsubscribe;
  };

  // Load menu items when restaurants change
  useEffect(() => {
    if (restaurants.length > 0) {
      loadAllMenuItems();
    }
  }, [restaurants]);

  // Load ALL restaurants (no island or active filtering) - with proper island field validation
  const loadAllRestaurants = () => {
    if (!db) return;

    const q = query(
      collection(db, 'restaurants'),
      orderBy('name')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const restaurantData = snapshot.docs.map(doc => {
        const data = doc.data();

        // Ensure island field exists and is valid
        const restaurant = {
          id: doc.id,
          ...data,
          island: data.island || 'saintKitts', // Default to saintKitts if no island specified
          isActive: data.isActive !== false, // Default to true if not specified
          isAcceptingOrders: data.isAcceptingOrders !== false // Default to true if not specified
        };

        return restaurant;
      });

      setRestaurants(restaurantData);
      saveCache('restaurants', restaurantData);
    });

    return unsubscribe;
  };

  // Load ALL menu items from ALL restaurant subcollections - matching iOS structure
  const loadAllMenuItems = async () => {
    if (!db || restaurants.length === 0) {
      setMenuItems([]);
      setLoading(false);
      return;
    }

    try {
      // Fetch every restaurant's menuItems subcollection in parallel —
      // sequential awaits made the page wait for N round-trips.
      const results = await Promise.all(
        restaurants.map(async (restaurant) => {
          try {
            const q = query(
              collection(db, 'restaurants', restaurant.id, 'menuItems'),
              orderBy('name')
            );

            const snapshot = await getDocs(q);
            return snapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data(),
              restaurantId: restaurant.id,
              restaurantName: restaurant.name,
              restaurantType: restaurant.restaurantType || 'Other',
              island: restaurant.island // This comes from the restaurant, not the menu item
            }));
          } catch (error) {
            // Skip restaurants that don't have menu items or have permission issues
            console.warn(`Could not load menu items for restaurant ${restaurant.id}:`, error);
            return [];
          }
        })
      );

      const allMenuItems = results.flat();
      setMenuItems(allMenuItems);
      saveCache('menuItems', allMenuItems);
      setLoading(false);
    } catch (error) {
      console.error('Error loading menu items:', error);
      setMenuItems([]);
      setLoading(false);
    }
  };

  // filteredMenuItems - matching iOS computed property with island filtering
  const filteredItems = (() => {
    // Start with all menu items
    let items = [...menuItems];

    // Filter items by restaurants on selected island - matching iOS approach
    if (selectedIsland !== 'all') {
      const firestoreIslandValue = currentIsland.firestoreValue;

      // Get restaurants that are active, accepting orders, and on the selected island
      // KFC is only shown on St. Kitts island
      const filteredRestaurants = restaurants.filter(restaurant => {
        const isOnSelectedIsland = restaurant.island === firestoreIslandValue;
        const isActiveAndAccepting = restaurant.isActive === true && restaurant.isAcceptingOrders === true;
        const isKFC = restaurant.restaurantType === 'KFC';

        // KFC only shows on St. Kitts
        if (isKFC && firestoreIslandValue !== 'St. Kitts') {
          return false;
        }

        return isOnSelectedIsland && isActiveAndAccepting;
      });

      // Get IDs of filtered restaurants
      const filteredRestaurantIds = new Set(filteredRestaurants.map(r => r.id));

      // Filter menu items to only include those from filtered restaurants
      items = items.filter(item => filteredRestaurantIds.has(item.restaurantId));
    }

    // Apply business type filter (Food/Retail)
    if (selectedBusinessType === 'food') {
      items = items.filter(item => isFoodRelated(item.restaurantType));
    } else if (selectedBusinessType === 'retail') {
      items = items.filter(item => !isFoodRelated(item.restaurantType));
    }

    // Apply search filter if needed
    if (searchText.trim() !== '') {
      const search = searchText.toLowerCase();
      items = items.filter(item =>
        item.name.toLowerCase().includes(search) ||
        (item.itemDescription && item.itemDescription.toLowerCase().includes(search)) ||
        (item.restaurantName && item.restaurantName.toLowerCase().includes(search))
      );
    }

    // Apply category filter if selected
    if (selectedCategory) {
      items = items.filter(item => item.category === selectedCategory);
    }

    // Sort by name
    return items.sort((a, b) => a.name.localeCompare(b.name));
  })();

  // Admin's Choice — featured items (isFeatured on the menuItems docs), split
  // into food vs retail for the two synced carousels. Island + active/accepting
  // restaurant gate mirrors filteredItems; the business-type toggle and search
  // don't apply — this is a showcase, not a filtered list.
  const { featuredFood, featuredRetail } = (() => {
    const firestoreIslandValue = currentIsland.firestoreValue;
    const eligible = new Set(
      restaurants
        .filter(r => r.island === firestoreIslandValue && r.isActive === true && r.isAcceptingOrders === true)
        .map(r => r.id)
    );
    const bannerById = new Map(restaurants.map(r => [r.id, r.bannerURL || '']));
    const featured = menuItems
      .filter(i => i.isFeatured === true && i.isAvailable !== false && eligible.has(i.restaurantId))
      .map(i => ({ ...i, restaurantBannerURL: bannerById.get(i.restaurantId) || '' }))
      .sort((a, b) =>
        (b.featuredAt?.toMillis?.() ?? b.featuredAt?.seconds ?? 0) -
        (a.featuredAt?.toMillis?.() ?? a.featuredAt?.seconds ?? 0));
    return {
      featuredFood: featured.filter(i => isFoodRelated(i.restaurantType)),
      featuredRetail: featured.filter(i => !isFoodRelated(i.restaurantType)),
    };
  })();

  // Group items by restaurant type - matching iOS MenuItemsView groupedItems
  const groupedItems = filteredItems.reduce((acc, item) => {
    const type = item.restaurantType || 'Other';
    if (!acc[type]) acc[type] = [];
    acc[type].push(item);
    return acc;
  }, {});

  // Interleave items across businesses within each type — round-robin one card
  // per business, fullest menu first, so one business can't flood a row.
  const interleaveByRestaurant = (items) => {
    const buckets = {};
    items.forEach(item => {
      (buckets[item.restaurantId] ||= []).push(item);
    });
    const order = Object.keys(buckets).sort(
      (a, b) => buckets[b].length - buckets[a].length || a.localeCompare(b)
    );
    const result = [];
    let i = 0;
    while (result.length < items.length) {
      order.forEach(rid => {
        if (i < buckets[rid].length) result.push(buckets[rid][i]);
      });
      i++;
    }
    return result;
  };
  Object.keys(groupedItems).forEach(type => {
    groupedItems[type] = interleaveByRestaurant(groupedItems[type]);
  });

  // iOS drops the KFC section on Nevis
  if (currentIsland.id === 'nevis') {
    delete groupedItems['KFC'];
  }

  // Only show types that actually have items, in iOS order
  const sortedRestaurantTypes = restaurantTypes.filter(t => groupedItems[t]);

  // Average of approved reviews per restaurant (iOS loadRestaurantRatings:
  // average when reviews exist, 5.0 otherwise) — drives the card's rating badge.
  const [restaurantRatings, setRestaurantRatings] = useState({});
  useEffect(() => {
    if (!db) return undefined;
    const q = query(collection(db, 'reviews'), where('status', '==', 'Approved'));
    return onSnapshot(q, (snap) => {
      const sums = {};
      snap.docs.forEach((d) => {
        const { restaurantId, rating } = d.data();
        if (!restaurantId || typeof rating !== 'number') return;
        sums[restaurantId] = sums[restaurantId] || { total: 0, count: 0 };
        sums[restaurantId].total += rating;
        sums[restaurantId].count += 1;
      });
      const avgs = {};
      Object.entries(sums).forEach(([id, s]) => { avgs[id] = s.total / s.count; });
      setRestaurantRatings(avgs);
    }, () => { /* ratings are cosmetic — fall back to 5.0 */ });
  }, [db]);

  const restaurantsWithRating = useMemo(
    () => restaurants.map(r => ({ ...r, rating: restaurantRatings[r.id] ?? 5.0 })),
    [restaurants, restaurantRatings]
  );

  // Get restaurant for item - now works with ALL restaurants
  const getRestaurantForItem = (item) => {
    // Find the restaurant by ID from ALL restaurants
    const restaurant = restaurantsWithRating.find(r => r.id === item.restaurantId);
    if (restaurant) {
      return restaurant;
    }

    // If not found, create a placeholder using the restaurant type
    return {
      id: item.restaurantId,
      name: item.restaurantType,
      description: `Specializing in ${item.restaurantType}`,
      address: 'Address information not available',
      island: selectedIsland,
      phoneNumber: 'Contact not available',
      email: '',
      openingHours: 'Hours may vary',
      isActive: true,
      isAcceptingOrders: false,
      isVerified: false,
      menuCategories: []
    };
  };

  // Restaurant type info — mirrors iOS restaurantType(for:) names,
  // iconName(forRestaurantType:) SF Symbols → lucide, and descriptions
  const getRestaurantTypeInfo = (type) => {
    const iconMap = {
      'KFC': { icon: Flame, name: 'KFC', description: 'Fried chicken, buckets, sides' },
      'Fast Food': { icon: UtensilsCrossed, name: 'Fast Food', description: 'Burgers, fries, local dishes' },
      'Bar & Grill': { icon: Wine, name: 'Bar & Grill', description: 'Grilled dishes, steaks, wings' },
      'Pizza': { icon: Pizza, name: 'Pizza', description: 'Pizza, pasta, calzones' },
      'Bakery': { icon: Cake, name: 'Bakery', description: 'Bread, pastries, cakes' },
      'Cafe': { icon: Coffee, name: 'Cafe', description: 'Coffee, eggs, omelets, pancakes' },
      'Juice Stand': { icon: GlassWater, name: 'Juice Stand', description: 'Lemonade, coconut water, cane juice' },
      'BBQ Grill': { icon: UtensilsCrossed, name: 'BBQ Grill', description: 'Barbecue chicken, ribs, pigtail' },
      'Seafood Restaurant': { icon: Fish, name: 'Seafood Restaurant', description: 'Fish, shrimp, lobster' },
      'Asian': { icon: UtensilsCrossed, name: 'Asian', description: 'Chinese, Japanese, Thai, noodles' },
      'Indian': { icon: UtensilsCrossed, name: 'Indian', description: 'Halal, gyro, shawarma, naan' },
      'Latin': { icon: UtensilsCrossed, name: 'Latin', description: 'Tacos, burritos, Latin cuisine' },
      'Mexican': { icon: UtensilsCrossed, name: 'Mexican', description: 'Tacos, burritos, Mexican cuisine' },
      'Sushi': { icon: UtensilsCrossed, name: 'Sushi', description: 'Sushi rolls, sashimi, bento boxes' },
      'Steak House': { icon: UtensilsCrossed, name: 'Steak House', description: 'Premium steaks, grilled meats' },
      'Local Restaurant': { icon: UtensilsCrossed, name: 'Local Restaurant', description: 'Local dishes, comfort food' },
      'Roti/Patty': { icon: UtensilsCrossed, name: 'Roti/Patty', description: 'Locally made chicken roti and patties' },
      'Vegetarian': { icon: Leaf, name: 'Vegetarian', description: 'Plant-based, vegan, salads' },
      'Snack Boxes': { icon: Share, name: 'Snack Boxes', description: 'Combo meals, snack boxes, platters' },
      'Fish & Meat': { icon: Fish, name: 'Fish & Meat', description: 'Fresh fish, butchered meat' },
      'Retail Store': { icon: ShoppingBag, name: 'Retail Store', description: 'General retail, everyday items' },
      'Electronics Store': { icon: Monitor, name: 'Electronics Store', description: 'Phones, computers, electronics' },
      'Home Goods': { icon: Home, name: 'Home Goods', description: 'Furniture, appliances, tools' },
      'Home Goods / Hardware Store': { icon: Home, name: 'Home Goods', description: 'Furniture, appliances, tools' },
      'Beauty Supply': { icon: StarIcon, name: 'Beauty Supply', description: 'Hair care, wigs, nails, makeup' },
      'Perfume Store': { icon: Sparkles, name: 'Perfume Store', description: 'Perfumes, colognes, fragrances' },
      'Jewelry Store': { icon: Sparkles, name: 'Jewelry Store', description: 'Rings, necklaces, watches' },
      'Pharmacy': { icon: UtensilsCrossed, name: 'Pharmacy', description: 'Pharmacy, health, wellness' },
      'Farmer': { icon: Leaf, name: 'Farmer', description: 'Fresh produce, vegetables, fruits' },
      'Supermarket': { icon: ShoppingCart, name: 'Supermarket', description: 'Groceries, household items, dairy' },
      'Craft & Souvenirs': { icon: Paintbrush, name: 'Craft & Souvenirs', description: 'Local crafts, souvenirs' },
      'Liquor Store & Smoke Shop': { icon: Wine, name: 'Liquor/Smoke', description: 'Duty free liquor, wines, cigars' },
      'Water Supply': { icon: Droplet, name: 'Water Supply', description: 'Bottled water, ice, delivery' },
      'Car Parts': { icon: Wrench, name: 'Car Parts', description: 'Auto parts, tires, accessories' }
    };

    return iconMap[type] || { icon: UtensilsCrossed, name: type, description: '' };
  };

  // Color for restaurant type — mirrors iOS colorForRestaurantType exactly
  const colorForRestaurantType = (type) => {
    const colorMap = {
      'fast food': '#FF3B30',
      'cafe': '#AF52DE',
      'bar & grill': '#FF3B30',
      'pizza': '#FFCC00',
      'asian': '#339966',
      'indian': '#E6801A',
      'bbq grill': '#4D66CC',
      'seafood restaurant': '#00B3CC',
      'beauty supply': '#E66699',
      'retail store': '#994DCC',
      'farmer': '#4DB34D',
      'supermarket': '#CC8033',
      'bakery': '#B36633',
      'fish & meat': '#CC3333',
      'fish/meat market': '#CC3333',
      'kfc': '#E63333',
      'juice stand': '#66CC99',
      'craft & souvenirs': '#664DB3',
      'liquor store & smoke shop': '#803399',
      'roti/patty': '#D98C26',
      'perfume store': '#CC80B3',
      'jewelry store': '#E6CC33',
      'electronics store': '#3380CC',
      'home goods': '#99664D',
      'home goods / hardware store': '#99664D',
      'water supply': '#3399E6',
      'vegetarian': '#66CC66',
      'snack boxes': '#F29933',
      'car parts': '#FF3B30',
      'pharmacy': '#B3334D',
      'latin': '#CC664D',
      'mexican': '#D94D4D',
      'sushi': '#4D9980',
      'steak house': '#BF4040',
      'local restaurant': '#5973B3'
    };
    return colorMap[(type || '').toLowerCase()] || '#8E8E93';
  };

  // Handle favorite toggle with guest check
  const handleToggleFavorite = (item) => {
    if (!currentUser) {
      navigate('/login');
      return;
    }
    toggleFavorite(item);
  };

  return (
    <div className="bg-[#F2F2F7] min-h-full pb-28">
      {/* ChefHeader("Menu") + globe map button + islandSelectionButton on the right */}
      <ChefHeader
        title="Menu"
        right={
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowMap(true)}
              aria-label="Map"
              className="flex items-center justify-center w-9 h-8 bg-gray-100 rounded-xl text-[17px] leading-none"
            >
              🌎
            </button>
            <IslandPicker />
          </div>
        }
      />

      {/* Promotional videos carousel */}
      <div className="px-[7px]">
        <PromotionalVideosView selectedIsland={currentIsland.firestoreValue} />
      </div>

      {/* iOS SearchBar — gray rounded field */}
      <div className="px-[7px] pt-2">
        <div className="flex items-center gap-2 bg-[#E5E5EA] rounded-[10px] px-3 py-2">
          <Search size={17} className="text-[#8E8E93] shrink-0" />
          <input
            type="text"
            placeholder="Search menu items..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            className="flex-1 min-w-0 bg-transparent text-[15px] text-gray-900 placeholder-[#8E8E93] focus:outline-none"
          />
          {searchText !== '' && (
            <button onClick={() => setSearchText('')} aria-label="Clear search">
              <CircleX size={17} className="text-[#8E8E93]" />
            </button>
          )}
        </div>
      </div>

      {/* Business type filter (Food/Retail) — iOS BusinessTypeButton pair */}
      <div className="px-[7px] py-1 flex">
        <BusinessTypeButton
          title="Food"
          icon={UtensilsCrossed}
          isSelected={selectedBusinessType === 'food'}
          onClick={() => setSelectedBusinessType('food')}
        />
        <BusinessTypeButton
          title="Retail"
          icon={ShoppingBag}
          isSelected={selectedBusinessType === 'retail'}
          onClick={() => setSelectedBusinessType('retail')}
        />
      </div>

      {/* Admin's Choice — featured carousel follows the Food/Retail toggle:
          only the section matching the selected type is shown */}
      <AdminsChoiceView foodItems={featuredFood} retailItems={featuredRetail} businessType={selectedBusinessType} />

      {/* Offline notice — iOS blocks ordering in MenuItemDetailView, not browsing */}
      {isAppOffline && (
        <div className="mx-[7px] my-2 bg-red-50 border border-red-200 rounded-xl px-4 py-3 flex items-center gap-2">
          <AlertCircle size={18} className="text-[#FF3B30] shrink-0" />
          <p className="text-[13px] text-red-700">We're currently offline. Please check back later.</p>
        </div>
      )}

      {/* Menu content — MenuItemsView grouped by restaurant type */}
      {filteredItems.length === 0 ? (
        /* iOS emptyStateView */
        <div className="flex flex-col items-center justify-center py-10 px-8 gap-4">
          {selectedBusinessType === 'food' ? (
            <UtensilsCrossed size={48} className="text-gray-400" strokeWidth={1.5} />
          ) : (
            <ShoppingBag size={48} className="text-gray-400" strokeWidth={1.5} />
          )}
          <h2 className="text-[17px] font-bold text-gray-900">
            {selectedBusinessType === 'food' ? 'No Food Available' : 'No Retail Available'}
          </h2>
          <p className="text-[15px] text-[#8E8E93] text-center">
            {selectedBusinessType === 'food'
              ? 'There are no food businesses available on this island right now.'
              : 'There are no retail businesses available on this island right now.'}
          </p>
        </div>
      ) : (
        <div className="pt-4 space-y-4">
          {sortedRestaurantTypes.map(restaurantType => (
            <RestaurantTypeSection
              key={restaurantType}
              restaurantTypeString={restaurantType}
              items={groupedItems[restaurantType] || []}
              getRestaurantForItem={getRestaurantForItem}
              getRestaurantTypeInfo={getRestaurantTypeInfo}
              colorForRestaurantType={colorForRestaurantType}
              isFavorite={isFavorite}
              onToggleFavorite={handleToggleFavorite}
              selectedBusinessType={selectedBusinessType}
            />
          ))}
        </div>
      )}

      {/* Floating cart button — iOS .overlay(alignment: .bottomTrailing) */}
      {cartItems.length > 0 && (
        <button
          onClick={() => setShowCartSheet(true)}
          className="fixed bottom-[100px] right-4 z-50 w-14 h-14 rounded-full bg-[#FF3B30] shadow-lg flex items-center justify-center active:scale-95 transition-transform"
          aria-label="Open cart"
        >
          <ShoppingCart size={24} className="text-white" />
          {getItemCount() > 0 && (
            <span className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-[#34C759] text-white text-xs font-bold flex items-center justify-center">
              {getItemCount() > 99 ? '99+' : getItemCount()}
            </span>
          )}
        </button>
      )}

      {/* CartView sheet — iOS .presentationDetents([.medium, .large]) */}
      <CartSheet open={showCartSheet} onClose={() => setShowCartSheet(false)} />

      {showMap && (
        <RestaurantMapModal
          restaurants={mapRestaurants}
          island={currentIsland.firestoreValue}
          businessType={selectedBusinessType}
          isFoodType={isFoodRelated}
          onClose={() => setShowMap(false)}
        />
      )}

      {/* iOS loadingView overlay — "Loading items..." */}
      {loading && (
        <div className="fixed inset-0 z-40 flex items-center justify-center pointer-events-none">
          <div className="bg-white rounded-lg shadow-lg px-5 py-4 flex items-center gap-3">
            <div className="w-5 h-5 rounded-full border-2 border-gray-200 border-t-[#FF3B30] animate-spin" />
            <span className="text-[15px] text-gray-900">Loading items...</span>
          </div>
        </div>
      )}

    </div>
  );
}

// iOS CustomerBusinessTypeButton — two big adjacent buttons,
// selected = filled red, unselected = systemGray6
function BusinessTypeButton({ title, icon: Icon, isSelected, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`flex-1 flex items-center justify-center gap-1.5 px-6 py-1.5 rounded-lg text-xs font-medium transition-colors ${
        isSelected ? 'bg-[#FF3B30] text-white' : 'bg-[#F2F2F7] text-gray-900'
      }`}
    >
      <Icon size={12} />
      {title}
    </button>
  );
}

// MARK: - RestaurantTypeSection (iOS MenuView.swift)
function RestaurantTypeSection({
  restaurantTypeString,
  items,
  getRestaurantForItem,
  getRestaurantTypeInfo,
  colorForRestaurantType,
  isFavorite,
  onToggleFavorite,
  selectedBusinessType
}) {
  const navigate = useNavigate();
  const typeInfo = getRestaurantTypeInfo(restaurantTypeString);
  const sectionColor = colorForRestaurantType(restaurantTypeString);
  const IconComponent = typeInfo.icon;

  // iOS previewItems — first 40 items of this type
  const previewItems = items.filter(i => i.restaurantType === restaurantTypeString).slice(0, 40);
  const restaurantCount = new Set(items.map(item => item.restaurantId).filter(Boolean)).size;

  return (
    <div className="space-y-2">
      {/* Section header — icon + name + description + View All */}
      <div className="flex items-center gap-1.5 px-[7px]">
        <IconComponent size={22} style={{ color: sectionColor }} className="shrink-0" />
        <span
          className="text-[17px] font-semibold truncate"
          style={{ color: sectionColor }}
        >
          {typeInfo.name}
        </span>
        {typeInfo.description && (
          <span className="text-xs text-gray-900 truncate min-w-0">
            {typeInfo.description}
          </span>
        )}
        <span className="flex-1" />
        {items.length > 4 && (
          <button
            onClick={() => navigate(`/restaurant-type/${encodeURIComponent(restaurantTypeString)}?businessType=${selectedBusinessType || 'food'}`)}
            className="text-xs px-2 py-1 rounded-lg shrink-0 font-normal"
            style={{ color: sectionColor, backgroundColor: `${sectionColor}1A` }}
          >
            View All ({restaurantCount})
          </button>
        )}
      </div>

      {/* Horizontal scrollable cards */}
      {previewItems.length > 0 ? (
        <div className="flex gap-3 overflow-x-auto px-[7px] scrollbar-hide">
          {previewItems.map(item => {
            if (item.restaurantType !== restaurantTypeString) return null;
            const restaurant = getRestaurantForItem(item);
            return (
              <MenuItemCard
                key={item.id}
                item={item}
                restaurant={restaurant}
                rating={restaurant.rating}
                isFavorite={isFavorite(item.id)}
                showHeart={false}
                onToggleFavorite={() => onToggleFavorite(item)}
                onClick={() => {
                  const restaurantId = item.restaurantId;
                  if (restaurantId) {
                    navigate(`/restaurant/${restaurantId}?businessType=${selectedBusinessType || 'food'}`);
                  }
                }}
              />
            );
          })}
        </div>
      ) : (
        /* iOS per-section empty state */
        <div className="mx-[7px] flex flex-col items-center gap-2 py-10 rounded-xl bg-gradient-to-br from-gray-400/30 to-gray-400/50">
          <UtensilsCrossed size={36} className="text-gray-400" />
          <p className="text-[15px] font-semibold text-gray-500">No menu items available</p>
          <p className="text-xs text-gray-500">Check back soon for new items</p>
        </div>
      )}

      {/* Divider */}
      <div className="border-b border-black/10 mx-[7px]" />
    </div>
  );
}

// MARK: - MenuItemCard — mirrors iOS MenuItemCard exactly:
// 160x100 card, image bg, black gradient, footnote name,
// green caption price, heart, star rating badge top-right.
export const MenuItemCard = React.memo(function MenuItemCard({ item, restaurant, rating, isFavorite, onToggleFavorite, onClick, showHeart = true }) {
  // iOS truncatedName: first two words if name > 7 chars and > 2 words
  const truncatedName = (() => {
    const words = (item.name || '').split(' ');
    if ((item.name || '').length > 7 && words.length > 2) {
      return words.slice(0, 2).join(' ');
    }
    return item.name;
  })();

  // iOS imageURL: menuItemImageURL → restaurant.bannerURL → empty
  const imageURL = item.menuItemImageURL || restaurant?.bannerURL || '';

  return (
    <div
      onClick={onClick}
      className="relative w-[160px] h-[100px] shrink-0 rounded-xl overflow-hidden bg-white cursor-pointer shadow-[0_2px_5px_rgba(0,0,0,0.1)] ring-1 ring-red-500/50"
    >
      {/* Image — fit inside the 160x100 frame (no crop), blurred cover of the
          same image fills the letterbox so portrait uploads stay clean */}
      {imageURL ? (
        <>
          <img
            src={imageURL}
            alt=""
            aria-hidden
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover blur-md scale-125 opacity-80"
          />
          <img
            src={imageURL}
            alt={item.name}
            loading="lazy"
            className="absolute inset-0 w-full h-full object-contain"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        </>
      ) : (
        <div className="absolute inset-0 bg-gray-400/30 flex items-center justify-center">
          <svg className="w-10 h-10 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        </div>
      )}

      {/* Gradient: clear → black/30 @45% → black/80 */}
      <div
        className="absolute inset-0"
        style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.3) 55%, transparent 100%)' }}
      />

      {/* Out of Stock badge */}
      {item.trackStock === true && (item.stockCount ?? 0) <= 0 && (
        <div className="absolute inset-0 flex items-center justify-center z-10 bg-black/40">
          <span className="px-2.5 py-1 rounded-lg bg-black/70 text-white text-[10px] font-bold uppercase tracking-wide">
            Out of Stock
          </span>
        </div>
      )}

      {/* Business name top-left (rating stays top-right) */}
      <div
        className={`absolute top-2 left-2 z-10 px-1.5 py-[3px] bg-red-600/75 rounded-lg text-[9px] font-bold text-white leading-tight truncate ${
          rating != null ? 'max-w-[98px]' : 'max-w-[144px]'
        }`}
      >
        {restaurant?.name || item.restaurantName || 'Local business'}
      </div>

      {/* Rating overlay top-right */}
      {rating != null && (
        <div className="absolute top-2 right-2 flex items-center gap-0.5 px-1.5 py-[3px] bg-black/60 rounded-lg z-10">
          <Star size={11} className="text-yellow-400 fill-yellow-400" />
          <span className="text-[11px] font-semibold text-white">{Number(rating).toFixed(1)}</span>
        </div>
      )}

      {/* Bottom overlay content — name drops to the price line on the home
          menu (heart hidden); two-line layout on the detail view */}
      <div className="absolute inset-x-0 bottom-0 p-3 flex flex-col gap-1">
        {showHeart && (
          <h3 className="text-[13px] font-semibold text-white leading-tight truncate drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)]">
            {truncatedName}
          </h3>
        )}
        <div className="flex items-end justify-between gap-1.5">
          {!showHeart && (
            <h3 className="text-[13px] font-semibold text-white leading-tight truncate drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)] min-w-0">
              {truncatedName}
            </h3>
          )}
          <span className={`text-[11px] font-bold text-[#30D158] drop-shadow-[0_1px_0_rgba(0,0,0,0.8)] ${showHeart ? '' : 'ml-auto shrink-0'}`}>
            ${item.price?.toFixed(2) || '0.00'}
          </span>
          {showHeart && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onToggleFavorite?.();
              }}
              className="drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)]"
              aria-label="Favorite"
            >
              <Heart
                size={16}
                className={isFavorite ? 'text-[#FF3B30]' : 'text-white'}
                fill={isFavorite ? '#FF3B30' : 'none'}
              />
            </button>
          )}
        </div>
      </div>
    </div>
  );
});

export default MenuPage;
